import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PageTitle from "../../components/PageTitle/PageTitle";
import RbacAdminService from "./Locality/Service/rbacAdminService";
import { showAppConfirm } from '../../context/AlertContext';

const ROLE_CODES = ["SUPERADMIN", "SCHOOLE", "TEACHER", "PARENT"];
const ACTIONS = ["read", "create", "update", "delete"];

const rowsOf = (res) => {
  const data = res?.data || res || {};
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
};

export default function UsersAndRoles() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin =
    String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState("");

  const notify = useCallback((msg) => setToast(String(msg || "")), []);
  const notifyError = useCallback(
    (err) =>
      setToast(
        err?.response?.data?.message || err?.message || "Something went wrong"
      ),
    []
  );

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  const runSeed = async () => {
    // eslint-disable-next-line no-alert
    if (!await showAppConfirm("Run RBAC seed? This upserts roles, resources, permissions and screens.")) {
      return;
    }
    try {
      await RbacAdminService.runSeed();
      notify("Seed completed. Users must log out and log in again to pick up new screens.");
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box p={3}>
      <PageTitle
        title="Users & Roles"
        button={
          <Button
            variant="outlined"
            color="primary"
            onClick={runSeed}
            style={{ textTransform: "none" }}
          >
            Run seed
          </Button>
        }
      />

      <Card style={{ marginTop: 14 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Roles" />
          <Tab label="Resources" />
          <Tab label="Role Permissions" />
          <Tab label="Screens" />
          <Tab label="API Endpoints" />
        </Tabs>
        <CardContent>
          {tab === 0 && <RolesTab notify={notify} notifyError={notifyError} />}
          {tab === 1 && <ResourcesTab notify={notify} notifyError={notifyError} />}
          {tab === 2 && <PermissionsTab notify={notify} notifyError={notifyError} />}
          {tab === 3 && <ScreensTab notify={notify} notifyError={notifyError} />}
          {tab === 4 && <ApiEndpointsTab notify={notify} notifyError={notifyError} />}
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast("")}
        message={toast}
      />
    </Box>
  );
}

// ——— Roles ———
function RolesTab({ notify, notifyError }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", isActive: true });
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(rowsOf(await RbacAdminService.listRoles()));
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(false);
    setForm({ code: "", name: "", isActive: true });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(true);
    setForm({ code: row.code, name: row.name, isActive: row.isActive !== false });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await RbacAdminService.updateRole(form.code, {
          name: form.name,
          isActive: form.isActive,
        });
      } else {
        await RbacAdminService.createRole(form);
      }
      setOpen(false);
      notify("Saved.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!await showAppConfirm(`Delete role ${row.code}?`)) return;
    try {
      await RbacAdminService.deleteRole(row.code);
      notify("Deleted.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={openNew} style={{ textTransform: "none" }}>
          Add Role
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Edit</TableCell>
            <TableCell align="right">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id || row.code}>
              <TableCell>{row.code}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.isActive === false ? "No" : "Yes"}</TableCell>
              <TableCell align="right">
                <EditIcon style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
              </TableCell>
              <TableCell align="right">
                <DeleteIcon style={{ cursor: "pointer" }} onClick={() => remove(row)} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} style={{ color: "#6b7280" }}>
                No roles yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? "Edit Role" : "Add Role"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <TextField
              variant="outlined"
              label="Code"
              value={form.code}
              disabled={editing}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={save} disabled={!form.code || !form.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ——— Resources ———
function ResourcesTab({ notify, notifyError }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", module: "general", isActive: true });

  const load = useCallback(async () => {
    try {
      setRows(rowsOf(await RbacAdminService.listResources()));
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(false);
    setForm({ code: "", name: "", module: "general", isActive: true });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(true);
    setForm({
      code: row.code,
      name: row.name,
      module: row.module || "general",
      isActive: row.isActive !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await RbacAdminService.updateResource(form.code, {
          name: form.name,
          module: form.module,
          isActive: form.isActive,
        });
      } else {
        await RbacAdminService.createResource(form);
      }
      setOpen(false);
      notify("Saved.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!await showAppConfirm(`Delete resource ${row.code}?`)) return;
    try {
      await RbacAdminService.deleteResource(row.code);
      notify("Deleted.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={openNew} style={{ textTransform: "none" }}>
          Add Resource
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Module</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Edit</TableCell>
            <TableCell align="right">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id || row.code}>
              <TableCell>{row.code}</TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.module}</TableCell>
              <TableCell>{row.isActive === false ? "No" : "Yes"}</TableCell>
              <TableCell align="right">
                <EditIcon style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
              </TableCell>
              <TableCell align="right">
                <DeleteIcon style={{ cursor: "pointer" }} onClick={() => remove(row)} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} style={{ color: "#6b7280" }}>
                No resources yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? "Edit Resource" : "Add Resource"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <TextField
              variant="outlined"
              label="Code"
              value={form.code}
              disabled={editing}
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Module"
              value={form.module}
              onChange={(e) => setForm({ ...form, module: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={save} disabled={!form.code || !form.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ——— Role Permissions (matrix) ———
function PermissionsTab({ notify, notifyError }) {
  const [roleCode, setRoleCode] = useState("SUPERADMIN");
  const [matrix, setMatrix] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (code) => {
      try {
        const res = await RbacAdminService.getMatrix(code);
        const data = res?.data || res || {};
        setMatrix(Array.isArray(data?.matrix) ? data.matrix : []);
      } catch (err) {
        notifyError(err);
      }
    },
    [notifyError]
  );

  useEffect(() => {
    load(roleCode);
  }, [roleCode, load]);

  const toggle = (idx, action) => {
    setMatrix((prev) =>
      prev.map((m, i) =>
        i === idx ? { ...m, [`can${action[0].toUpperCase()}${action.slice(1)}`]: !m[`can${action[0].toUpperCase()}${action.slice(1)}`] } : m
      )
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = matrix.map((m) => ({
        roleCode,
        resourceCode: m.resourceCode,
        canRead: Boolean(m.canRead),
        canCreate: Boolean(m.canCreate),
        canUpdate: Boolean(m.canUpdate),
        canDelete: Boolean(m.canDelete),
      }));
      await RbacAdminService.bulkUpsertRolePermissions(items);
      notify("Permissions saved.");
      load(roleCode);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gridGap={16} flexWrap="wrap">
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel id="perm-role-label">Role</InputLabel>
          <Select
            labelId="perm-role-label"
            value={roleCode}
            label="Role"
            onChange={(e) => setRoleCode(e.target.value)}
          >
            {ROLE_CODES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={saving}
          style={{ textTransform: "none" }}
        >
          Save Permissions
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Resource</TableCell>
            <TableCell>Module</TableCell>
            {ACTIONS.map((a) => (
              <TableCell key={a} align="center" style={{ textTransform: "capitalize" }}>
                {a}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {matrix.map((m, idx) => (
            <TableRow key={m.resourceCode}>
              <TableCell>{m.resourceName || m.resourceCode}</TableCell>
              <TableCell>{m.module}</TableCell>
              {ACTIONS.map((a) => {
                const key = `can${a[0].toUpperCase()}${a.slice(1)}`;
                return (
                  <TableCell key={a} align="center">
                    <Checkbox
                      checked={Boolean(m[key])}
                      onChange={() => toggle(idx, a)}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {matrix.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} style={{ color: "#6b7280" }}>
                No resources to map.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ——— Screens ———
function ScreensTab({ notify, notifyError }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    _id: "",
    path: "",
    label: "",
    resourceCode: "",
    roles: [],
    requiredAction: "read",
    isActive: true,
  });

  const load = useCallback(async () => {
    try {
      setRows(rowsOf(await RbacAdminService.listScreens()));
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(false);
    setForm({
      _id: "",
      path: "",
      label: "",
      resourceCode: "",
      roles: [],
      requiredAction: "read",
      isActive: true,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(true);
    setForm({
      _id: row._id,
      path: row.path,
      label: row.label,
      resourceCode: row.resourceCode,
      roles: row.roles || [],
      requiredAction: row.requiredAction || "read",
      isActive: row.isActive !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        path: form.path,
        label: form.label,
        resourceCode: form.resourceCode,
        roles: form.roles,
        requiredAction: form.requiredAction,
        isActive: form.isActive,
      };
      if (editing) {
        await RbacAdminService.updateScreen(form._id, body);
      } else {
        await RbacAdminService.createScreen(body);
      }
      setOpen(false);
      notify("Saved. Affected users must log out and log in again.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  const ensureProgressGridScreen = async () => {
    const path = "/app/activitymatrix";
    const payload = {
      path,
      label: "Activity Progress Grid",
      resourceCode: "activity",
      roles: ["SCHOOLE", "TEACHER"],
      requiredAction: "read",
      isActive: true,
    };
    try {
      const existing = rows.find((r) => String(r.path || "").trim() === path);
      if (existing?._id) {
        await RbacAdminService.updateScreen(existing._id, {
          ...payload,
          roles: Array.from(
            new Set([...(existing.roles || []), ...payload.roles]),
          ),
        });
      } else {
        await RbacAdminService.createScreen(payload);
      }
      notify(
        "Progress Grid screen saved for SCHOOLE + TEACHER. Those users must log out and log in again.",
      );
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!await showAppConfirm(`Delete screen ${row.path}?`)) return;
    try {
      await RbacAdminService.deleteScreen(row._id);
      notify("Deleted.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box>
      <Box
        mb={2}
        p={2}
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
        }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>
          Grant Progress Grid access
        </Typography>
        <Typography variant="body2" style={{ color: "#475569", marginBottom: 10 }}>
          1) Click <strong>Ensure Progress Grid</strong> (or Run seed on this page),{" "}
          2) confirm roles include <strong>SCHOOLE</strong> and <strong>TEACHER</strong>,{" "}
          3) those users must <strong>log out and log in</strong> so{" "}
          <code>rbac_screens</code> refreshes. Path:{" "}
          <code>/app/activitymatrix</code>, resource: <code>activity</code>.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={ensureProgressGridScreen}
          style={{ textTransform: "none" }}
        >
          Ensure Progress Grid
        </Button>
      </Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={openNew} style={{ textTransform: "none" }}>
          Add Screen
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Path</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Resource</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Edit</TableCell>
            <TableCell align="right">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id || row.path}>
              <TableCell>{row.path}</TableCell>
              <TableCell>{row.label}</TableCell>
              <TableCell>{row.resourceCode}</TableCell>
              <TableCell>{(row.roles || []).join(", ")}</TableCell>
              <TableCell>{row.isActive === false ? "No" : "Yes"}</TableCell>
              <TableCell align="right">
                <EditIcon style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
              </TableCell>
              <TableCell align="right">
                <DeleteIcon style={{ cursor: "pointer" }} onClick={() => remove(row)} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} style={{ color: "#6b7280" }}>
                No screens yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit Screen" : "Add Screen"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <TextField
              variant="outlined"
              label="Path"
              value={form.path}
              onChange={(e) => setForm({ ...form, path: e.target.value })}
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Resource Code"
              value={form.resourceCode}
              onChange={(e) => setForm({ ...form, resourceCode: e.target.value.toLowerCase() })}
              fullWidth
            />
            <FormControl variant="outlined">
              <InputLabel id="screen-roles-label">Roles</InputLabel>
              <Select
                labelId="screen-roles-label"
                multiple
                value={form.roles}
                label="Roles"
                onChange={(e) => setForm({ ...form, roles: e.target.value })}
                renderValue={(selected) => selected.join(", ")}
              >
                {ROLE_CODES.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Checkbox checked={form.roles.indexOf(c) > -1} />
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel id="screen-action-label">Required Action</InputLabel>
              <Select
                labelId="screen-action-label"
                value={form.requiredAction}
                label="Required Action"
                onChange={(e) => setForm({ ...form, requiredAction: e.target.value })}
              >
                {ACTIONS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={!form.path || !form.label || !form.resourceCode}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ——— API Endpoints ———
function ApiEndpointsTab({ notify, notifyError }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    _id: "",
    method: "GET",
    pathPattern: "",
    resourceCode: "",
    action: "read",
    isActive: true,
  });

  const load = useCallback(async () => {
    try {
      setRows(rowsOf(await RbacAdminService.listApiEndpoints()));
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(false);
    setForm({
      _id: "",
      method: "GET",
      pathPattern: "",
      resourceCode: "",
      action: "read",
      isActive: true,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(true);
    setForm({
      _id: row._id,
      method: row.method,
      pathPattern: row.pathPattern,
      resourceCode: row.resourceCode,
      action: row.action,
      isActive: row.isActive !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        method: form.method,
        pathPattern: form.pathPattern,
        resourceCode: form.resourceCode,
        action: form.action,
        isActive: form.isActive,
      };
      if (editing) {
        await RbacAdminService.updateApiEndpoint(form._id, body);
      } else {
        await RbacAdminService.createApiEndpoint(body);
      }
      setOpen(false);
      notify("Saved.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!await showAppConfirm(`Delete endpoint ${row.method} ${row.pathPattern}?`)) return;
    try {
      await RbacAdminService.deleteApiEndpoint(row._id);
      notify("Deleted.");
      load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box>
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={openNew} style={{ textTransform: "none" }}>
          Add Endpoint
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Method</TableCell>
            <TableCell>Path Pattern</TableCell>
            <TableCell>Resource</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Edit</TableCell>
            <TableCell align="right">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id}>
              <TableCell>{row.method}</TableCell>
              <TableCell>{row.pathPattern}</TableCell>
              <TableCell>{row.resourceCode}</TableCell>
              <TableCell>{row.action}</TableCell>
              <TableCell>{row.isActive === false ? "No" : "Yes"}</TableCell>
              <TableCell align="right">
                <EditIcon style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
              </TableCell>
              <TableCell align="right">
                <DeleteIcon style={{ cursor: "pointer" }} onClick={() => remove(row)} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} style={{ color: "#6b7280" }}>
                No API endpoints yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit Endpoint" : "Add Endpoint"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <FormControl variant="outlined">
              <InputLabel id="ep-method-label">Method</InputLabel>
              <Select
                labelId="ep-method-label"
                value={form.method}
                label="Method"
                onChange={(e) => setForm({ ...form, method: e.target.value })}
              >
                {["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              variant="outlined"
              label="Path Pattern"
              value={form.pathPattern}
              onChange={(e) => setForm({ ...form, pathPattern: e.target.value })}
              placeholder="/api/teachernotes/list"
              fullWidth
            />
            <TextField
              variant="outlined"
              label="Resource Code"
              value={form.resourceCode}
              onChange={(e) => setForm({ ...form, resourceCode: e.target.value.toLowerCase() })}
              fullWidth
            />
            <FormControl variant="outlined">
              <InputLabel id="ep-action-label">Action</InputLabel>
              <Select
                labelId="ep-action-label"
                value={form.action}
                label="Action"
                onChange={(e) => setForm({ ...form, action: e.target.value })}
              >
                {ACTIONS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={!form.method || !form.pathPattern || !form.resourceCode || !form.action}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
